// src/components/achievements/MementoCard.tsx
// 拍立得風格的成就卡片 - Memento 紀念物設計
//
// 設計理念：
// - 靈感來自拍立得照片，賦予每個成就「紀念物」的收藏價值
// - 視覺層級：Icon > Title > Date > Ripples
// - Apple 風格：簡潔、溫暖、情感化

import React from 'react';
import { motion } from 'framer-motion';
import { Objective } from '../../features/goal/types/objective';
import { getCategoryConfig } from '../../features/goal/utils/objectiveUtils';
import { toDateObject } from '../../utils/dateUtils';
import { format } from 'date-fns';
import { CERAMIC_VARIANTS, DEFAULT_VARIANT } from '../../features/goal/constants/categoryStyles';

interface MementoCardProps {
  objective: Objective;
  onClick: () => void;
}

/**
 * MementoCard - 拍立得風格成就卡片 (Ceramic Gallery Edition)
 */
const MementoCard: React.FC<MementoCardProps> = ({ objective, onClick }) => {
  const categoryConfig = getCategoryConfig(objective.category);
  const CategoryIcon = categoryConfig.icon;

  const categoryKey = objective.category || 'growth';
  const variant = CERAMIC_VARIANTS[categoryKey] || DEFAULT_VARIANT;

  // 格式化完成日期
  const completedDate = objective.completedAt
    ? format(toDateObject(objective.completedAt), 'MMM d, yyyy')
    : 'Unknown';

  return (
    <motion.button
      onClick={onClick}
      className={`w-full text-center flex flex-col items-center
                 p-4 rounded-xl
                 group relative
                 transition-colors duration-300 cursor-pointer
                 focus:outline-none ${variant.cardHoverBg}`}
      aria-label={`View journey for ${objective.title}`}
      initial="rest"
      whileHover="hover"
      animate="rest"
    >
      {/* Flat Medal Container (Gradient + Border) */}
      <div className="relative mb-4">
        <motion.div
          className={`w-24 h-24 rounded-full relative overflow-hidden border-4 ${variant.bgClass} ${variant.borderClass.replace('border-', 'border-opacity-30 border-')} ${variant.borderClass}`}
          variants={{
            rest: { scale: 1 },
            hover: { scale: 1.05 }
          }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          style={{
            boxShadow: 'inset -1px -1px 2px rgba(255,255,255,0.8), inset 1px 1px 2px rgba(0,0,0,0.05), 2px 2px 4px rgba(0,0,0,0.1), -1px -1px 2px rgba(255,255,255,0.5)',
          }}
        >
          {/* Icon Layer */}
          <div className="absolute inset-0 flex items-center justify-center z-content">
            <CategoryIcon
              size={40}
              className={`${variant.color} ${variant.shadow} transition-all duration-300 group-hover:brightness-110`}
              strokeWidth={2}
            />
          </div>
        </motion.div>
      </div>

      {/* 資訊區域 */}
      <div className="space-y-1 relative z-10 transition-opacity duration-300">
        {/* 標題 */}
        <h3 className="text-sm font-semibold text-gray-700 line-clamp-1 leading-tight px-1 group-hover:text-gray-900 transition-colors">
          {objective.title}
        </h3>

        {/* 完成日期 */}
        <p className="text-[10px] text-gray-400 font-medium tracking-wide uppercase">
          {completedDate}
        </p>
      </div>
    </motion.button>
  );
};

export default MementoCard;
